import { MutationTree } from 'vuex';
import { AuxData, State } from '@/types/vuex/state';
import { Mutations } from '@/types/vuex/mutations';
import { swap } from '@/util/ArrayHelper';
import { PhoneButtons } from '@/types/api';
import Vue from 'vue';
import { priceToString } from '@/util/CurrencyHelper';

export enum MutationTypes {
    SetItemsPerPage = 'SET_ITEMS_PER_PAGE',
    SetCheckIfNeedToLoadMoreItems = 'SET_CHECK_IF_NEED_TO_LOAD_ITEMS',
    SetIsComparing = 'SET_IS_COMPARING',
    SetItems = 'SET_ITEMS',
    SetTickedItems = 'SET_TICKED_ITEMS',
    ComparingItemsInitialized = 'COMPARING_ITEMS_INITIALIZED',
    SetMenuTechSpecOpened = 'SET_MENU_TECH_SPEC_OPENED',
    SetMenuSellerOpened = 'SET_MENU_SELLER_OPENED',
    SetEmptyContentDivId = 'SET_EMPTY_CONTENT_DIV_ID',
    SetAuxData = 'SET_AUX_DATA',
    SetWhatsappHint = 'SET_WHATSAPP_HINT',
    SetApiUrl = 'SET_API_URL',
    FetchingVehicleComparisonData = 'FETCHING_VEHICLE_COMPARISON_DATA',
    VehicleComparisonDataFetched = 'VEHICLE_COMPARISON_DATA_FETCHED',
    ComparisonIsLoading = 'COMPARISON_IS_LOADING',
    ListIsLoading = 'LIST_IS_LOADING',
    ListFinishedLoading = 'LIST_FINISHED_LOADING',
    NewListPageIsLoading = 'NEW_LIST_PAGE_IS_LOADING',
    NewListPageLoaded = 'NEW_LIST_PAGE_LOADED',
    GettingItemsInfo = 'GETTING_ITEMS_INFO',
    ItemsInfoFetched = 'ITEMS_INFO_FETCHED',
    ItemsShortInfoFetched = 'ITEMS_SHORT_INFO_FETCHED',
    ListDataFetched = 'LIST_DATA_FETCHED',
    ApiErrorOccurred = 'API_ERROR_OCCURRED',
    SetIsDisplayingDifference = 'SET_IS_DISPLAYING_DIFFERENCE',
    NormalizeItemState = 'NORMALIZE_ITEM_STATE',
    SwapItems = 'SWAP_ITEMS',
    OpenModal = 'OPEN_MODAL',
    CloseModal = 'CLOSE_MODAL',
    OpenDeleteCarModal = 'OPEN_DELETE_CAR_MODAL',
    CloseDeleteCarModal = 'CLOSE_DELETE_CAR_MODAL',
    OpenIncludedOptionsModal = 'OPEN_INCLUDED_OPTIONS_MODAL',
    CloseIncludedOptionsModal = 'CLOSE_INCLUDED_OPTIONS_MODAL',
    IncludedOptionsFetched = 'INCLUDED_OPTIONS_FETCHED',
    SetIsMobile = 'SET_IS_MOBILE',
    SetComparingItemsFromBackend = 'SET_COMPARING_ITEMS_FROM_BACKEND',
    ClearComparingItemsFromBackend = 'CLEAR_COMPARING_ITEMS_FROM_BACKEND',
    SetIsEmptyTechSpec = 'SET_IS_EMPTY_TECH_SPEC',
    DeleteItemData = 'DELETE_ITEM_DATA',
}

export const mutations: MutationTree<State> & Mutations = {
    [MutationTypes.SetItemsPerPage] (state, itemsPerPage) {
        state.itemsPerPage = itemsPerPage;
    },
    [MutationTypes.SetCheckIfNeedToLoadMoreItems] (state, needCheck) {
        state.checkIfNeedToLoadMoreItems = needCheck;
    },
    [MutationTypes.SetIsComparing] (state, isComparing) {
        state.isComparing = isComparing;
    },
    [MutationTypes.SetItems] (state, items) {
        state.items = items;
    },
    [MutationTypes.SetTickedItems] (state, items) {
        state.tickedItems = items;
    },
    [MutationTypes.ComparingItemsInitialized] (state) {
        state.comparingItemInitialized = true;
    },
    [MutationTypes.SetMenuSellerOpened] (state, isOpened) {
        state.menuSellerOpened = isOpened;
    },
    [MutationTypes.SetMenuTechSpecOpened] (state, isOpened) {
        state.menuTechSpecOpened = isOpened;
    },
    [MutationTypes.SetEmptyContentDivId] (state, divId) {
        state.emptyContentDivId = divId;
    },
    [MutationTypes.SetAuxData] (state, auxData) {
        state.auxData = JSON.parse(auxData) as AuxData;
    },
    [MutationTypes.SetWhatsappHint] (state, whatsappHint) {
        state.whatsappHint = whatsappHint;
    },
    [MutationTypes.SetApiUrl] (state, divId) {
        state.apiUrl = divId;
    },
    [MutationTypes.ComparisonIsLoading] (state) {
        state.isComparisonLoading = true;
    },
    [MutationTypes.ListIsLoading] (state) {
        state.isListLoading = true;
    },
    [MutationTypes.FetchingVehicleComparisonData] (state, index) {
        state.fetchingVehicleData[index] = true;
    },
    [MutationTypes.VehicleComparisonDataFetched] (state, index) {
        state.fetchingVehicleData[index] = false;
    },
    [MutationTypes.ItemsInfoFetched] (state, data) {
        state.comparisonCache = { ...state.comparisonCache, ...data };
        state.isComparisonLoading = false;
    },
    [MutationTypes.ItemsShortInfoFetched] (state, data) {
        state.shortInfo = data;
        state.isComparisonLoading = false;
    },
    [MutationTypes.ListDataFetched] (state, data) {
        data.forEach(listItem => {
            Vue.set(state.listCache, +listItem.id, { html: listItem.content, url: listItem.url });
        });
    },
    [MutationTypes.ListFinishedLoading] (state) {
        state.isListLoading = false;
    },
    [MutationTypes.NewListPageIsLoading] (state) {
        state.isNewListPageLoading = true;
    },
    [MutationTypes.NewListPageLoaded] (state) {
        state.isNewListPageLoading = false;
    },
    [MutationTypes.ApiErrorOccurred] (state) {
        state.isComparisonLoading = false;
        state.isListLoading = false;
        state.error = true;
    },
    [MutationTypes.SetIsDisplayingDifference] (state, isDisplaying) {
        state.isDisplayingDifference = isDisplaying;
    },
    [MutationTypes.NormalizeItemState] (state, payload) {
        const i = payload.index;
        const data = state.comparisonCache[state.tickedItems[payload.index]];

        Vue.set(state.prices, i, payload.clear ? '' : data?.Main?.Price ? priceToString(data.Main.Price) : '');
        Vue.set(state.tradePrice, i, payload.clear ? '' : data?.Main?.TradePrice ? priceToString(data.Main.TradePrice) : '');
        Vue.set(state.vats, i, payload.clear ? '' : data?.Main?.Price?.Vat ?? '');
        Vue.set(state.onos, i, payload.clear ? '' : data?.Main?.Price?.ONO ?? '');
        Vue.set(state.years, i, payload.clear ? '' : data?.Main?.Year ?? '');
        Vue.set(state.makes, i, payload.clear ? '' : data?.Main?.Make ?? '');
        Vue.set(state.models, i, payload.clear ? '' : data?.Main?.Model ?? '');
        Vue.set(state.variants, i, payload.clear ? '' : data?.Main?.Variant ?? '');
        Vue.set(state.photos, i, payload.clear ? '' : data?.Main?.Photo ?? '');
        Vue.set(state.mileages, i, payload.clear ? '' : data?.Main?.Mileage ?? '');
        Vue.set(state.colors, i, payload.clear ? '' : data?.Main?.Colour ?? '');
        Vue.set(state.fuelTypes, i, payload.clear ? '' : data?.Main?.FuelType ?? '');
        Vue.set(state.transmissions, i, payload.clear ? '' : data?.Main?.Transmission ?? '');
        Vue.set(state.doors, i, payload.clear ? '' : data?.Main?.Doors ?? '');
        Vue.set(state.bodyStyles, i, payload.clear ? '' : data?.Main?.BodyStyle ?? '');
        Vue.set(state.engineSizes, i, payload.clear ? '' : data?.Main?.EngineSize ?? '');
        Vue.set(state.standardTaxes, i, payload.clear ? '' : data?.Main?.StandardTax ?? '');
        Vue.set(state.standardTaxCallouts, i, payload.clear ? '' : data?.Main?.StandardTaxCalloutText ?? '');
        Vue.set(state.additionalTaxes, i, payload.clear ? '' : data?.Main?.AdditionalTax ?? '');
        Vue.set(state.additionalTaxCallouts, i, payload.clear ? '' : data?.Main?.AdditionalTaxCalloutText ?? '');
        Vue.set(state.warranties, i, payload.clear ? '' : data?.Main?.Warranty ?? '');
        Vue.set(state.insurances, i, payload.clear ? '' : data?.Main?.Insurance ?? '');
        Vue.set(state.insurancesCallouts, i, payload.clear ? '' : data?.Main?.InsuranceCalloutText ?? '');
        Vue.set(state.insurancesLinks, i, payload.clear ? '' : data?.Main?.InsuranceLink ?? '');
        Vue.set(state.approvalSchemeLinks, i, payload.clear ? '' : data?.Main?.ApprovalScheme.Link ?? '');
        Vue.set(state.approvalSchemeLogos, i, payload.clear ? '' : data?.Main?.ApprovalScheme.Logo ?? '');
        Vue.set(state.approvalSchemeNames, i, payload.clear ? '' : data?.Main?.ApprovalScheme.Name ?? '');
        Vue.set(state.owners, i, payload.clear ? '' : data?.Main?.Owners?.toString() ?? '');
        Vue.set(state.hpiHistoryLink, i, payload.clear ? '' : data?.Main?.HpiHistoryLink?.toString() ?? '');
        Vue.set(state.urls, i, payload.clear ? '' : data?.Main?.Url ?? '');
        Vue.set(state.tradeRegDate, i, payload.clear ? '' : data?.Main?.TradeRegDate ?? '');
        Vue.set(state.tradeKeys, i, payload.clear ? '' : data?.Main?.TradeKeys ?? '');
        Vue.set(state.tradeTaxBook, i, payload.clear ? '' : data?.Main?.TradeTaxBook ?? '');

        if (!payload.clear && data?.Main?.HPIAdvisoryStatuses?.length) {
            data.Main.HPIAdvisoryStatuses.forEach(status => {
                switch (status.Type) {
                case 'Stolen':
                    Vue.set(state.stolen, i, status.Caption);
                    Vue.set(state.stolenCallouts, i, status.CalloutText);
                    break;
                case 'Scrapped':
                    Vue.set(state.scrapped, i, status.Caption);
                    Vue.set(state.scrappedCallouts, i, status.CalloutText);
                    break;
                case 'Write-Off':
                    Vue.set(state.writeOff, i, status.Caption);
                    Vue.set(state.writeOffCallouts, i, status.CalloutText);
                    break;
                }
            });
        } else {
            Vue.set(state.stolen, i, '');
            Vue.set(state.stolenCallouts, i, '');
            Vue.set(state.scrapped, i, '');
            Vue.set(state.scrappedCallouts, i, '');
            Vue.set(state.writeOff, i, '');
            Vue.set(state.writeOffCallouts, i, '');
        }

        Vue.set(state.expertReviewLinks, i, payload.clear ? '' : data?.Main?.VehicleRating.ExpertReviewLink ?? '');
        Vue.set(state.expertRatings, i, payload.clear ? '' : data?.Main?.VehicleRating.ExpertRating ?? '');
        Vue.set(state.customerReviewLinks, i, payload.clear ? '' : data?.Main?.VehicleRating.CustomerReviewLink ?? '');
        Vue.set(state.customerRatings, i, payload.clear ? '' : data?.Main?.VehicleRating.CustomerRating ?? '');

        Vue.set(state.enginePower, i, payload.clear ? '' : data?.TechSpec?.EnginePower ?? '');
        Vue.set(state.acceleration, i, payload.clear ? '' : data?.TechSpec?.Acceleration ?? '');
        Vue.set(state.topSpeed, i, payload.clear ? '' : data?.TechSpec?.TopSpeed ?? '');
        Vue.set(state.fuelConsumption, i, payload.clear ? '' : data?.TechSpec?.FuelConsumption ?? '');
        Vue.set(state.fuelTankCapacity, i, payload.clear ? '' : data?.TechSpec?.FuelTankCapacity ?? '');
        Vue.set(state.combinedRange, i, payload.clear ? '' : data?.TechSpec?.CombinedRange ?? '');
        Vue.set(state.co2Emissions, i, payload.clear ? '' : data?.TechSpec?.CO2Emissions ?? '');
        Vue.set(state.numberOfSeats, i, payload.clear ? '' : data?.TechSpec?.NumberOfSeats ?? '');
        Vue.set(state.length, i, payload.clear ? '' : data?.TechSpec?.Length ?? '');
        Vue.set(state.width, i, payload.clear ? '' : data?.TechSpec?.Width ?? '');
        Vue.set(state.height, i, payload.clear ? '' : data?.TechSpec?.Height ?? '');
        Vue.set(state.weight, i, payload.clear ? '' : data?.TechSpec?.WeightMinimumKerb ?? '');
        Vue.set(state.luggage, i, payload.clear ? '' : data?.TechSpec?.LuggageCapacity ?? '');
        Vue.set(state.wheelType, i, payload.clear ? '' : data?.TechSpec?.WheelType ?? '');
        Vue.set(state.loadHeight, i, payload.clear ? '' : data?.TechSpec?.LoadHeight ?? '');
        Vue.set(state.loadLength, i, payload.clear ? '' : data?.TechSpec?.LoadLength ?? '');
        Vue.set(state.payload, i, payload.clear ? '' : data?.TechSpec?.Payload ?? '');
        Vue.set(state.towingWeight, i, payload.clear ? '' : data?.TechSpec?.TowingWeight ?? '');
        Vue.set(state.isVan, i, payload.clear ? '' : data?.Main?.IsVan ?? '');

        Vue.set(state.sellers, i, payload.clear ? '' : data?.Seller?.Name ?? '');
        Vue.set(state.sellerLogos, i, payload.clear ? '' : data?.Seller?.Logo ?? '');
        Vue.set(state.dealerRatings, i, payload.clear ? '' : data?.Seller?.DealerRating ?? '');
        Vue.set(state.dealerReviewLinks, i, payload.clear ? '' : data?.Seller?.ReviewsLink ?? '');
        Vue.set(state.dealerReviewCounts, i, payload.clear ? '' : data?.Seller?.DealerReviewCount ?? '');
        Vue.set(state.addresses, i, payload.clear ? '' : data?.Seller?.Address ?? '');
        Vue.set(state.townes, i, payload.clear ? '' : data?.Seller?.Town ?? '');
        Vue.set(state.postcodes, i, payload.clear ? '' : data?.Seller?.Postcode ?? '');
        Vue.set(state.mapsLinks, i, payload.clear ? '' : data?.Seller?.ViewOnMapLink ?? '');
        Vue.set(state.emailEnquiryLinks, i, payload.clear ? '' : data?.Seller?.EmailEnquiryLink ?? '');
        Vue.set(state.pxEnquiryLinks, i, payload.clear ? '' : data?.Seller?.PXEnquiryLink ?? '');
        Vue.set(state.testDriveLinks, i, payload.clear ? '' : data?.Seller?.TestDriveLink ?? '');
        Vue.set(state.phones, i, payload.clear ? '' : data?.Seller?.Phones ?? '');
        Vue.set(state.phoneButtons, i, payload.clear ? {} as PhoneButtons : data?.Seller?.PhoneButtons ?? {} as PhoneButtons);
        Vue.set(state.ownerIds, i, payload.clear ? '' : data?.Seller?.Id ?? '');
        Vue.set(state.smsMessages, i, payload.clear ? '' : data?.Seller?.SmsMessageText ?? '');
        Vue.set(state.facebookIds, i, payload.clear ? '' : data?.Seller?.FacebookUID ?? '');
        Vue.set(state.isOpened, i, payload.clear ? '' : data?.Seller?.IsDealershipOpened ?? '');
        Vue.set(state.hasIncludedOptions, i, payload.clear ? '' : data?.TechSpec?.HasIncludedOptions ?? '');
    },
    [MutationTypes.SwapItems] (state, payload) {
        state.prices = swap(state.prices, payload.index1, payload.index2);
        state.tradePrice = swap(state.tradePrice, payload.index1, payload.index2);
        state.vats = swap(state.vats, payload.index1, payload.index2);
        state.onos = swap(state.onos, payload.index1, payload.index2);
        state.years = swap(state.years, payload.index1, payload.index2);
        state.makes = swap(state.makes, payload.index1, payload.index2);
        state.models = swap(state.models, payload.index1, payload.index2);
        state.variants = swap(state.variants, payload.index1, payload.index2);
        state.photos = swap(state.photos, payload.index1, payload.index2);
        state.mileages = swap(state.mileages, payload.index1, payload.index2);
        state.colors = swap(state.colors, payload.index1, payload.index2);
        state.fuelTypes = swap(state.fuelTypes, payload.index1, payload.index2);
        state.transmissions = swap(state.transmissions, payload.index1, payload.index2);
        state.doors = swap(state.doors, payload.index1, payload.index2);
        state.bodyStyles = swap(state.bodyStyles, payload.index1, payload.index2);
        state.engineSizes = swap(state.engineSizes, payload.index1, payload.index2);
        state.standardTaxes = swap(state.standardTaxes, payload.index1, payload.index2);
        state.standardTaxCallouts = swap(state.standardTaxCallouts, payload.index1, payload.index2);
        state.additionalTaxes = swap(state.additionalTaxes, payload.index1, payload.index2);
        state.additionalTaxCallouts = swap(state.additionalTaxCallouts, payload.index1, payload.index2);
        state.warranties = swap(state.warranties, payload.index1, payload.index2);
        state.insurances = swap(state.insurances, payload.index1, payload.index2);
        state.insurancesCallouts = swap(state.insurancesCallouts, payload.index1, payload.index2);
        state.insurancesLinks = swap(state.insurancesLinks, payload.index1, payload.index2);
        state.approvalSchemeLinks = swap(state.approvalSchemeLinks, payload.index1, payload.index2);
        state.approvalSchemeLogos = swap(state.approvalSchemeLogos, payload.index1, payload.index2);
        state.approvalSchemeNames = swap(state.approvalSchemeNames, payload.index1, payload.index2);
        state.owners = swap(state.owners, payload.index1, payload.index2);
        state.stolen = swap(state.stolen, payload.index1, payload.index2);
        state.stolenCallouts = swap(state.stolenCallouts, payload.index1, payload.index2);
        state.scrapped = swap(state.scrapped, payload.index1, payload.index2);
        state.scrappedCallouts = swap(state.scrappedCallouts, payload.index1, payload.index2);
        state.writeOff = swap(state.writeOff, payload.index1, payload.index2);
        state.writeOffCallouts = swap(state.writeOffCallouts, payload.index1, payload.index2);
        state.hpiHistoryLink = swap(state.hpiHistoryLink, payload.index1, payload.index2);
        state.expertReviewLinks = swap(state.expertReviewLinks, payload.index1, payload.index2);
        state.expertRatings = swap(state.expertRatings, payload.index1, payload.index2);
        state.customerRatings = swap(state.customerRatings, payload.index1, payload.index2);
        state.customerReviewLinks = swap(state.customerReviewLinks, payload.index1, payload.index2);

        state.enginePower = swap(state.enginePower, payload.index1, payload.index2);
        state.acceleration = swap(state.acceleration, payload.index1, payload.index2);
        state.topSpeed = swap(state.topSpeed, payload.index1, payload.index2);
        state.fuelConsumption = swap(state.fuelConsumption, payload.index1, payload.index2);
        state.fuelTankCapacity = swap(state.fuelTankCapacity, payload.index1, payload.index2);
        state.combinedRange = swap(state.combinedRange, payload.index1, payload.index2);
        state.co2Emissions = swap(state.co2Emissions, payload.index1, payload.index2);
        state.numberOfSeats = swap(state.numberOfSeats, payload.index1, payload.index2);
        state.length = swap(state.length, payload.index1, payload.index2);
        state.width = swap(state.width, payload.index1, payload.index2);
        state.height = swap(state.height, payload.index1, payload.index2);
        state.weight = swap(state.weight, payload.index1, payload.index2);
        state.luggage = swap(state.luggage, payload.index1, payload.index2);
        state.wheelType = swap(state.wheelType, payload.index1, payload.index2);
        state.loadHeight = swap(state.loadHeight, payload.index1, payload.index2);
        state.loadLength = swap(state.loadLength, payload.index1, payload.index2);
        state.payload = swap(state.payload, payload.index1, payload.index2);
        state.towingWeight = swap(state.towingWeight, payload.index1, payload.index2);
        state.isVan = swap(state.isVan, payload.index1, payload.index2);

        state.sellers = swap(state.sellers, payload.index1, payload.index2);
        state.sellerLogos = swap(state.sellerLogos, payload.index1, payload.index2);
        state.dealerRatings = swap(state.dealerRatings, payload.index1, payload.index2);
        state.dealerReviewLinks = swap(state.dealerReviewLinks, payload.index1, payload.index2);
        state.dealerReviewCounts = swap(state.dealerReviewCounts, payload.index1, payload.index2);
        state.addresses = swap(state.addresses, payload.index1, payload.index2);
        state.townes = swap(state.townes, payload.index1, payload.index2);
        state.postcodes = swap(state.postcodes, payload.index1, payload.index2);
        state.mapsLinks = swap(state.mapsLinks, payload.index1, payload.index2);
        state.emailEnquiryLinks = swap(state.emailEnquiryLinks, payload.index1, payload.index2);
        state.pxEnquiryLinks = swap(state.pxEnquiryLinks, payload.index1, payload.index2);
        state.testDriveLinks = swap(state.testDriveLinks, payload.index1, payload.index2);
        state.phones = swap(state.phones, payload.index1, payload.index2);
        state.phoneButtons = swap(state.phoneButtons, payload.index1, payload.index2);
        state.ownerIds = swap(state.ownerIds, payload.index1, payload.index2);
        state.smsMessages = swap(state.smsMessages, payload.index1, payload.index2);
        state.facebookIds = swap(state.facebookIds, payload.index1, payload.index2);
        state.isOpened = swap(state.isOpened, payload.index1, payload.index2);
        state.urls = swap(state.urls, payload.index1, payload.index2);
        state.hasIncludedOptions = swap(state.hasIncludedOptions, payload.index1, payload.index2);
        state.tradeRegDate = swap(state.tradeRegDate, payload.index1, payload.index2);
        state.tradeKeys = swap(state.tradeKeys, payload.index1, payload.index2);
        state.tradeTaxBook = swap(state.tradeTaxBook, payload.index1, payload.index2);
    },
    [MutationTypes.OpenModal] (state, payload) {
        state.modal.isOpened = true;
        state.modal.id = payload.id;
        state.modal.src = payload.src;
    },
    [MutationTypes.CloseModal] (state) {
        state.modal.isOpened = false;
    },
    [MutationTypes.OpenDeleteCarModal] (state, index) {
        state.deleteCarModal.isOpened = true;
        state.deleteCarModal.index = index;
    },
    [MutationTypes.CloseDeleteCarModal] (state) {
        state.deleteCarModal.isOpened = false;
        state.deleteCarModal.index = 0;
    },
    [MutationTypes.OpenIncludedOptionsModal] (state, index) {
        state.includedOptionsModal.isOpened = true;
        state.includedOptionsModal.index = index;
    },
    [MutationTypes.CloseIncludedOptionsModal] (state) {
        state.includedOptionsModal.isOpened = false;
        state.includedOptionsModal.index = 0;
    },
    [MutationTypes.IncludedOptionsFetched] (state, payload) {
        Vue.set(state.includedOptionsCache, payload.id, payload.options);
    },
    [MutationTypes.SetIsMobile] (state, isMobile) {
        state.isMobile = isMobile;
    },
    [MutationTypes.SetComparingItemsFromBackend] (state, items) {
        state.comparingItemsFromBackend = items;
    },
    [MutationTypes.ClearComparingItemsFromBackend] (state) {
        state.comparingItemsFromBackend = null;
    },
    [MutationTypes.SetIsEmptyTechSpec] (state, isEmpty) {
        state.isEmptyTechSpec = isEmpty;
    },
    [MutationTypes.DeleteItemData] (state, id) {
        const index = state.shortInfo.findIndex(item => item.id === id);
        if (index === -1) {
            return;
        }
        const shortInfo = [...state.shortInfo];
        shortInfo.splice(index, 1);
        state.shortInfo = shortInfo;
        if (state.comparisonCache[id]) {
            Vue.delete(state.comparisonCache, id);
        }
        if (state.includedOptionsCache[id]) {
            Vue.delete(state.includedOptionsCache, id);
        }
        if (state.listCache[id]) {
            Vue.delete(state.listCache, id);
        }
    }
};
